import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ChevronUp, ChevronDown, Maximize2, Minimize2, X, Copy, Check, CheckCircle2, CalendarClock,
  RefreshCcw, Pencil, Trash2, AlertTriangle, Folder,
} from 'lucide-react'
import {
  mediaLibraryApi, type MediaBrowseEntry, type MediaDetails, type MediaOpBlocker,
} from '../../lib/api.js'
import { Button } from '../ui/button.js'
import { Spinner } from '../common/Spinner.js'
import { MediaExtBadge } from './MediaExtBadge.js'
import { MediaPlayer } from './MediaPlayer.js'
import {
  formatAudio, formatBitrate, formatBroadcastSlot, formatBytesFr, formatDateTimeFr,
  formatScan, formatTimecode,
} from './mediaLibraryFormat.js'
import './MediaPreviewDialog.css'

const IMAGE_RE = /\.(png|jpe?g|gif|webp|bmp|svg)$/i
const VIDEO_RE = /\.(mp4|m4v|webm|mov|ogv)$/i // formats lisibles nativement par le navigateur
const AUDIO_RE = /\.(wav|mp3|flac|aac|m4a|ogg|aiff?)$/i

export interface MediaOpFailure { message: string; blockers?: MediaOpBlocker[] }

interface MediaPreviewDialogProps {
  entry:       MediaBrowseEntry
  position:    number
  total:       number
  /** Chemin réseau affiché du dossier courant (racine + sous-dossier). */
  folderLabel: string
  /** Point d'origine de l'ouverture (ligne cliquée), pour l'animation. */
  originY?:    number | null
  canModify:   boolean
  busy:        boolean
  failure:     MediaOpFailure | null
  onPrev:      () => void
  onNext:      () => void
  onClose:     () => void
  onRename:    (entry: MediaBrowseEntry) => void
  onDelete:    (entry: MediaBrowseEntry) => void
  onReplace:   (entry: MediaBrowseEntry, file: File) => void
}

/**
 * Fenêtre d'aperçu d'un fichier : lecteur, fiche technique, chemin CasparCG et
 * diffusions à venir. Les flèches passent au fichier voisin du dossier sans
 * refermer la fenêtre ; Échap la referme et rend le focus à la liste.
 */
export const MediaPreviewDialog = memo(function MediaPreviewDialog({
  entry, position, total, folderLabel, originY, canModify, busy, failure,
  onPrev, onNext, onClose, onRename, onDelete, onReplace,
}: MediaPreviewDialogProps) {
  const [details, setDetails] = useState<MediaDetails | null>(null)
  const [loadedVersion, setLoadedVersion] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [direction, setDirection] = useState<'up' | 'down' | null>(null)
  const dialogRef  = useRef<HTMLElement>(null)
  const closeRef   = useRef<HTMLButtonElement>(null)
  const replaceRef = useRef<HTMLInputElement>(null)
  const returnFocus = useRef<HTMLElement | null>(null)

  // Un remplacement conserve le chemin. La version disque, et non l'identité
  // de l'objet browse, invalide la fiche et le cache HTTP du flux (60 s).
  const mediaVersion = JSON.stringify([entry.relativePath, entry.modifiedAt, entry.size])
  const loading = loadedVersion !== mediaVersion

  // Ignorer aussi une réponse tardive de l'ancienne version du même fichier.
  useEffect(() => {
    let cancelled = false
    mediaLibraryApi.details(entry.relativePath, mediaVersion)
      .then(d => { if (!cancelled) { setDetails(d); setLoadedVersion(mediaVersion) } })
      .catch(() => { if (!cancelled) { setDetails(null); setLoadedVersion(mediaVersion) } })
    return () => { cancelled = true }
  }, [entry.relativePath, mediaVersion])

  // Focus : la fenêtre prend la main à l'ouverture et la rend à la fermeture.
  useEffect(() => {
    returnFocus.current = document.activeElement as HTMLElement | null
    closeRef.current?.focus()
    return () => { returnFocus.current?.focus?.() }
  }, [])

  const goPrev = useCallback(() => { if (position > 1) { setDirection('up'); onPrev() } }, [onPrev, position])
  const goNext = useCallback(() => { if (position < total) { setDirection('down'); onNext() } }, [onNext, position, total])

  const onKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return }
    const inField = (event.target as HTMLElement).closest('input, .media-player')
    if (!inField && event.key === 'ArrowUp') { event.preventDefault(); goPrev(); return }
    if (!inField && event.key === 'ArrowDown') { event.preventDefault(); goNext(); return }
    if (event.key !== 'Tab' || !dialogRef.current) return
    // Piège de focus : Tab boucle dans la fenêtre.
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]):not([type="file"]), [tabindex]:not([tabindex="-1"])',
    )).filter(el => el.offsetParent !== null)
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }, [goNext, goPrev, onClose])

  const copyPath = useCallback(() => {
    const value = (!loading ? details?.casparPath : null) ?? entry.casparPath ?? ''
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    })
  }, [details, entry.casparPath, loading])

  // Tant que la fiche du fichier affiché n'est pas arrivée, rien de l'ancien.
  const info = loading ? null : details
  const kind = IMAGE_RE.test(entry.name) ? 'image' : VIDEO_RE.test(entry.name) ? 'video' : AUDIO_RE.test(entry.name) ? 'audio' : 'other'
  const fps = info?.frameRate ?? 25
  const resolution = info?.resolution ? info.resolution.replace('x', ' × ') : null
  const scan = formatScan(info?.frameRate, info?.fieldOrder)
  const codec = info?.codec ? [info.codec, formatBitrate(info.bitrate)].filter(Boolean).join(' · ') : null
  const audio = formatAudio(info?.audioCodec, info?.audioLayout, info?.audioSampleRate)
  const broadcasts = info?.broadcasts

  return (
    <div
      className="media-preview-dialog__overlay"
      onKeyDown={onKeyDown}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <section
        ref={dialogRef}
        className={`media-preview-dialog${expanded ? ' is-expanded' : ''}`}
        style={originY != null ? { transformOrigin: `50% ${originY}px` } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-preview-title"
      >
        <header className="media-preview-dialog__bar">
          <MediaExtBadge name={entry.name} />
          <h2 id="media-preview-title" className="media-preview-dialog__title" title={entry.name}>{entry.name}</h2>
          <div className="media-preview-dialog__nav" role="group" aria-label="Fichier voisin">
            <button type="button" onClick={goPrev} disabled={position <= 1} aria-label="Fichier précédent" title="Fichier précédent (↑)">
              <ChevronUp size={14} aria-hidden />
            </button>
            <button type="button" onClick={goNext} disabled={position >= total} aria-label="Fichier suivant" title="Fichier suivant (↓)">
              <ChevronDown size={14} aria-hidden />
            </button>
          </div>
          <span className="media-preview-dialog__position" aria-live="polite">{position} / {total}</span>
          <span className="media-preview-dialog__sep" aria-hidden />
          <button type="button" className="media-preview-dialog__icon" onClick={() => setExpanded(x => !x)} aria-pressed={expanded} aria-label={expanded ? 'Réduire la fenêtre' : 'Agrandir la fenêtre'}>
            {expanded ? <Minimize2 size={15} aria-hidden /> : <Maximize2 size={15} aria-hidden />}
          </button>
          <button ref={closeRef} type="button" className="media-preview-dialog__icon" onClick={onClose} aria-label="Fermer l’aperçu">
            <X size={16} aria-hidden />
          </button>
        </header>

        <div key={entry.relativePath} className={`media-preview-dialog__body${direction ? ` is-from-${direction}` : ''}`}>
          <div className="media-preview-dialog__player">
            <MediaPlayer
              key={mediaVersion}
              src={`${mediaLibraryApi.streamUrl(entry.relativePath)}&v=${encodeURIComponent(mediaVersion)}`}
              kind={kind}
              alt={entry.name}
              fps={info?.frameRate}
              durationSec={info?.durationSec ?? entry.durationSec}
            />
          </div>

          <aside className="media-preview-dialog__meta" aria-label="Fiche technique">
            <section className="media-preview-dialog__section">
              <h3 className="media-preview-dialog__section-title">Fichier</h3>
              {loading && <div className="media-preview-dialog__loading"><Spinner /> Analyse…</div>}
              <dl className="media-preview-dialog__facts">
                <Fact label="Taille" value={formatBytesFr(entry.size)} />
                <Fact label="Durée" value={info?.durationSec != null ? formatTimecode(info.durationMs != null ? info.durationMs / 1000 : info.durationSec, fps) : null} />
                <Fact label="Résolution" value={resolution ? [resolution, scan].filter(Boolean).join(' · ') : null} />
                <Fact label="Codec" value={codec} />
                <Fact label="Conteneur" value={info?.container?.toUpperCase() ?? null} />
                <Fact label="Audio" value={audio} />
              </dl>
              {info?.error && <p className="media-preview-dialog__error" title={info.error}><AlertTriangle size={12} aria-hidden /> {info.error}</p>}
            </section>

            <section className="media-preview-dialog__section">
              <h3 className="media-preview-dialog__section-title">Chemin CasparCG</h3>
              <div className="media-preview-dialog__path">
                <code>{info?.casparPath ?? entry.casparPath ?? '—'}</code>
                <button type="button" className="media-preview-dialog__icon media-preview-dialog__icon--sm" onClick={copyPath} aria-label="Copier le chemin CasparCG" title={copied ? 'Copié' : 'Copier'}>
                  {copied ? <Check size={13} aria-hidden /> : <Copy size={13} aria-hidden />}
                </button>
              </div>
            </section>

            <section className="media-preview-dialog__section">
              <h3 className="media-preview-dialog__section-title">Diffusions</h3>
              {!broadcasts
                ? <div className="media-preview-dialog__sched">{loading ? '…' : 'Grille non consultée'}</div>
                : broadcasts.total === 0
                  ? (
                    <div className="media-preview-dialog__sched media-preview-dialog__sched--none">
                      <CheckCircle2 size={14} aria-hidden /> Aucune diffusion programmée
                    </div>
                  )
                  : (
                    <div className="media-preview-dialog__sched media-preview-dialog__sched--some">
                      <span className="media-preview-dialog__sched-head">
                        <CalendarClock size={14} aria-hidden />
                        {broadcasts.total} diffusion{broadcasts.total > 1 ? 's' : ''} programmée{broadcasts.total > 1 ? 's' : ''}
                      </span>
                      <ul className="media-preview-dialog__slots">
                        {broadcasts.upcoming.map(b => (
                          <li key={`${b.startTime}-${b.title}`}>
                            <span className="media-preview-dialog__slot-time">{formatBroadcastSlot(b.startTime)}</span>
                            <span className="media-preview-dialog__slot-title" title={`${b.title} · ${b.channelName}`}>{b.title}</span>
                          </li>
                        ))}
                      </ul>
                      {broadcasts.total > broadcasts.upcoming.length && (
                        <span className="media-preview-dialog__slots-more">et {broadcasts.total - broadcasts.upcoming.length} autre{broadcasts.total - broadcasts.upcoming.length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                  )}
            </section>
          </aside>
        </div>

        {failure && (
          <div className="media-preview-dialog__refusal" role="alert">
            <AlertTriangle size={15} aria-hidden />
            <div className="media-preview-dialog__refusal-text">
              <strong>{failure.message}</strong>
              {failure.blockers && failure.blockers.length > 0 && (
                <span>
                  {failure.blockers.map(b => `${formatBroadcastSlot(b.startTime)} · ${b.title}`).join(' — ')}
                </span>
              )}
            </div>
          </div>
        )}

        <footer className="media-preview-dialog__foot">
          <span className="media-preview-dialog__foot-meta" title={folderLabel}>
            <Folder size={12} aria-hidden />
            {entry.modifiedAt ? `Modifié le ${formatDateTimeFr(entry.modifiedAt).replace(' ', ' à ')} · ` : ''}{folderLabel}
          </span>
          {canModify && (
            <FooterActions>
              <input
                ref={replaceRef}
                type="file"
                hidden
                accept={entry.ext ?? undefined}
                onChange={e => {
                  const file = e.target.files?.[0]
                  e.target.value = ''
                  if (file) onReplace(entry, file)
                }}
              />
              {/* Un morceau s'ajoute par Infoservice › Réglages, qui le normalise. */}
              {kind !== 'audio' && (
                <Button variant="outline" onClick={() => replaceRef.current?.click()} disabled={busy}>
                  <RefreshCcw size={14} aria-hidden /> Remplacer le média
                </Button>
              )}
              <Button variant="outline" onClick={() => onRename(entry)} disabled={busy}>
                <Pencil size={14} aria-hidden /> Renommer
              </Button>
              <span className="media-preview-dialog__foot-sep" aria-hidden />
              <Button variant="destructive" onClick={() => onDelete(entry)} disabled={busy}>
                {busy ? <Spinner /> : <Trash2 size={14} aria-hidden />} Supprimer
              </Button>
            </FooterActions>
          )}
        </footer>
      </section>
    </div>
  )
})

const FooterActions = ({ children }: { children: ReactNode }) => (
  <div className="media-preview-dialog__actions">{children}</div>
)

const Fact = memo(function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="media-preview-dialog__fact">
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  )
})
